import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronDown, MessageSquare, Mail, Phone } from "lucide-react";
import useSEO from "@/lib/useSEO";

// 6 groups × 5 Q&A = 30 questions
const FAQ_GROUPS = [
  {
    id: "about",
    title: "About Sarthi Associates",
    subtitle: "Vision, expertise, coverage",
    items: [
      { q: "What is Sarthi Associates?", a: "India's frontline-workforce recruitment platform connecting verified skilled workers (welders, fitters, riggers, electricians, technicians, plumbers, scaffolders, wiremen, T&D workers, foremen, housekeepers, grass cutters, security guards) with EPC contractors, thermal power plants, alumina refineries and heavy industrial employers." },
      { q: "What industries does Sarthi specialise in?", a: "Thermal power plants, alumina refineries, oil & gas, cement, steel, solar/wind, and general EPC construction. Our Engineering Consultancy arm covers C&I, MOV/POV overhauling, switchyard maintenance, relay testing, calibration and loop checking." },
      { q: "Which regions do you cover?", a: "Pan-India. Active hiring today across Delhi NCR, Mumbai, Pune, Noida, Bengaluru, Hyderabad, Chennai, and Kolkata. Regional executive teams organised by Zone (North, South, East, West, Central) — see /executive-teams." },
      { q: "Who founded Sarthi Associates?", a: "Sarthi was founded in 2026 in Noida, Uttar Pradesh, by Managing Director Sanjay Singh Yadav (B.Tech Electrical, 14+ years in thermal power and alumina refineries), supported by Executive Directors Sudhanshu Pandey, Sandeep Kumar and Tanveer Siddiqui." },
      { q: "In which languages is Sarthi available?", a: "English, Hindi, Marathi and Tamil. The UI is voice-enabled to help low-literacy workers navigate the platform independently." },
    ],
  },
  {
    id: "candidates",
    title: "Candidate Support",
    subtitle: "Registration, trades, alerts, profile",
    items: [
      { q: "How do I register as a candidate?", a: "Go to /register, pick 'Worker', select your trade (welder, fitter, security guard, etc.), tap your years of experience, then verify with mobile OTP or email + password — takes about 30 seconds. Registration is 100% free." },
      { q: "How do I pick the right trade?", a: "Choose the trade that matches your ITI or on-site experience. If you hold multiple skills (e.g. a welder who can also rig), pick the primary one at registration and add secondary trades from your dashboard later." },
      { q: "How will I receive job alerts?", a: "New jobs matching your trade and city are pushed to WhatsApp (if opted-in), SMS and email. You can also browse /jobs directly and filter by 'Verified recruiters only' to see jobs from trusted employers first." },
      { q: "How do I update my profile or resume?", a: "Log in → Dashboard → Edit Profile. You can update your skills, experience, mobile number, WhatsApp preference and upload a photo/resume at any time." },
      { q: "Do I have to pay Sarthi to get a job?", a: "Never. Sarthi is free for candidates. Any recruiter or middleman asking a candidate for money in exchange for placement is violating our Terms — report them immediately to contact@sarthiassociates.com." },
    ],
  },
  {
    id: "recruiters",
    title: "Recruiter & Business Partner Portal",
    subtitle: "Posting jobs, verification, benefits",
    items: [
      { q: "How do I post a job?", a: "Register at /register as a 'Business Partner', complete company details and GST, then click 'Post a Job'. Once submitted, your listing appears immediately on /jobs and is delivered to matched candidates within minutes." },
      { q: "What are the benefits of being a Business Partner?", a: "Direct access to 1,500+ verified frontline workers, an integrated HR Dashboard with drag-and-drop hiring pipeline, application analytics, salary insights per trade + city, verified-recruiter badge, and priority placement in candidate feeds." },
      { q: "How can I get the 'Verified Recruiter' badge?", a: "Complete registration, submit company GST + a business email, and post at least one lawful job. Our admin team reviews and issues the green Verified badge — visible on every job card and job detail page." },
      { q: "How do I verify a candidate's skills?", a: "Every profile lists trade, ITI status and years of experience. Use the Kanban pipeline (/contractor/pipeline) to move candidates through Screening → Shortlist → Interview stages; you can call, WhatsApp or send interview notes directly from the card." },
      { q: "Can I download applications as CSV?", a: "Yes. Admin and Business Partners can export candidate lists as CSV from the HR Dashboard. Use it for offline shortlisting, hiring committee review, or upload to your ATS." },
    ],
  },
  {
    id: "consultancy",
    title: "Engineering Consultancy",
    subtitle: "Thermal Power, C&I, custom quotes",
    items: [
      { q: "What consultancy services do you offer?", a: "Thermal Power Plant design/testing/commissioning, switchyard maintenance, relay testing, MOV & POV overhauling, HT/LT motor overhauling, calibration of pressure/temperature/level transmitters, DCS/PLC loop checking (cold + hot), and elevator/lift maintenance." },
      { q: "How do I book a consultant?", a: "Visit /consultancy, pick a service card, and pay via Razorpay. Our team confirms your booking within 24 hours with a Statement of Work covering scope, timeline and deliverables." },
      { q: "Can I request a custom quote?", a: "Yes. For scopes not listed (large-scale mobilisation, multi-plant maintenance retainers, greenfield staffing), email contact@sarthiassociates.com or use the Tech Support chat — we respond with a custom proposal within 2 working days." },
      { q: "Do you support C&I projects specifically?", a: "Yes. Our Control & Instrumentation practice covers calibration, loop checking, DCS/PLC commissioning, relay testing and switchyard maintenance. Engineers hold NABL-traceable instruments and are available for on-site deployment." },
      { q: "What is Sarthi's liability during a consultancy engagement?", a: "Liability is capped at 100% of the fees paid for that specific Statement of Work. Site safety, statutory approvals and permit-to-work remain the client's responsibility. Full clauses in /disclaimer." },
    ],
  },
  {
    id: "billing",
    title: "Premium Plans & Payments",
    subtitle: "UPI, discount codes, refunds",
    items: [
      { q: "Which payment methods do you accept?", a: "All payments are processed via Razorpay — UPI (GPay, PhonePe, Paytm, BHIM), credit cards, debit cards, net banking and popular wallets. Invoices with GST are auto-generated and downloadable from Billing History." },
      { q: "What do Premium Plans include for recruiters?", a: "Featured job posts (boosted for 7 days), 'Blast' broadcasts to matched candidates, higher application volumes, weekly growth analytics, priority verified badge, and access to the drag-and-drop Kanban pipeline." },
      { q: "How do I use a discount code?", a: "Enter the code on the checkout screen — e.g. try SAVE80 for 80% off your first premium plan. Codes are single-use unless otherwise stated and stackable rules are shown on the checkout page." },
      { q: "What is your refund policy?", a: "Refunds are considered on a case-by-case basis within 7 days of payment for material non-performance by Sarthi. Send proof of payment and the reason to contact@sarthiassociates.com — we respond within 48 hours." },
      { q: "Do plans auto-renew?", a: "Yes, Premium subscriptions auto-renew unless cancelled at least 24 hours before the renewal date. You can cancel anytime from Dashboard → Billing → Manage Subscription." },
    ],
  },
  {
    id: "safety",
    title: "Safety & Privacy",
    subtitle: "Data security, OTP, disclaimers",
    items: [
      { q: "How is my data protected?", a: "TLS 1.3 in transit, encrypted databases at rest, bcrypt-hashed passwords, httpOnly + Secure cookies, IP rate-limiting and brute-force lockout. We follow DPDP Act 2023 and IT Act 2000. Full details in /privacy." },
      { q: "Do you sell my personal data?", a: "No. We never sell personal data. We share only the minimum required with trusted partners (Razorpay, Twilio, SendGrid, Google AdSense) for the services they provide. See /privacy §6 for the full list." },
      { q: "How does OTP verification work?", a: "A 6-digit OTP is delivered via Twilio SMS to your mobile with a 5-minute expiry. After 3 wrong attempts the code is invalidated. Never share your OTP with anyone — Sarthi staff will never ask for it." },
      { q: "What is Sarthi's role during industrial site work?", a: "Sarthi is a connector between candidate and employer. Unless we have signed a Manpower Deployment Agreement, the hosting Recruiter is the 'principal employer' under the Contract Labour Act 1970 and is responsible for site safety, PPE, insurance and statutory welfare." },
      { q: "Do you use cookies for advertising?", a: "Only with your consent. On first visit our cookie banner asks whether to allow analytics + advertising cookies (Google Analytics, AdSense). You can opt out any time via browser settings or Google Ad Settings." },
    ],
  },
];

export default function FAQ() {
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);

  useSEO({
    title: "Frequently Asked Questions — Sarthi Associates",
    description: "Answers to the 30+ most common questions about Sarthi Associates — registration, jobs, recruiter portal, Engineering Consultancy, Premium Plans, payments, privacy and site safety.",
    path: "/faq",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ_GROUPS.flatMap((g) => g.items).map((i) => ({
        "@type": "Question",
        "name": i.q,
        "acceptedAnswer": { "@type": "Answer", "text": i.a },
      })),
    },
  });

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return FAQ_GROUPS;
    return FAQ_GROUPS
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) => i.q.toLowerCase().includes(needle) || i.a.toLowerCase().includes(needle),
        ),
      }))
      .filter((g) => g.items.length);
  }, [q]);

  const totalMatch = filtered.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="faq-page">
      {/* Hero */}
      <header className="mb-8 text-center">
        <div className="mb-2 inline-block rounded-full bg-sarthi-accent px-3 py-1 text-xs font-extrabold uppercase tracking-widest text-sarthi-primary">
          Help Centre
        </div>
        <h1 className="font-display text-4xl font-black text-sarthi-text sm:text-5xl">Frequently Asked Questions</h1>
        <p className="mx-auto mt-3 max-w-2xl text-base font-semibold text-sarthi-muted">
          30+ answers on registration, jobs, Business Partner portal, Engineering Consultancy, Premium Plans and privacy.
        </p>
      </header>

      {/* Search */}
      <div className="mb-8">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-sarthi-muted" />
          <input
            data-testid="faq-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search questions… e.g. 'OTP', 'refund', 'business partner'"
            className="w-full rounded-2xl border-2 border-sarthi-border bg-white py-4 pl-12 pr-4 text-base font-semibold text-sarthi-text placeholder:font-medium placeholder:text-sarthi-muted focus:border-sarthi-primary focus:outline-none"
          />
        </div>
        {q && (
          <p className="mt-2 text-xs font-bold uppercase tracking-wide text-sarthi-muted">
            {totalMatch} {totalMatch === 1 ? "match" : "matches"} for "{q}"
          </p>
        )}
      </div>

      {/* Groups */}
      <div className="space-y-8">
        {filtered.map((g) => (
          <section key={g.id} data-testid={`faq-group-${g.id}`}>
            <div className="mb-3">
              <h2 className="font-display text-2xl font-extrabold text-sarthi-text">{g.title}</h2>
              <p className="text-sm font-semibold text-sarthi-muted">{g.subtitle}</p>
            </div>
            <div className="space-y-2">
              {g.items.map((item, idx) => {
                const id = `${g.id}-${idx}`;
                const isOpen = openId === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setOpenId(isOpen ? null : id)}
                    data-testid={`faq-item-${id}`}
                    aria-expanded={isOpen}
                    className={`sarthi-card block w-full p-4 text-left transition-all ${isOpen ? "ring-2 ring-sarthi-primary/40" : "hover:border-sarthi-primary/50"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-display text-base font-extrabold text-sarthi-text sm:text-lg">{item.q}</span>
                      <ChevronDown className={`mt-1 h-5 w-5 flex-none text-sarthi-primary transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                    {isOpen && (
                      <p className="mt-2 text-sm font-medium leading-relaxed text-sarthi-muted sm:text-[15px]" data-testid={`faq-answer-${id}`}>
                        {item.a}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
        {filtered.length === 0 && (
          <div className="sarthi-card p-8 text-center" data-testid="faq-no-results">
            <p className="font-display text-lg font-extrabold text-sarthi-text">No matches yet.</p>
            <p className="mt-1 text-sm font-medium text-sarthi-muted">Try a shorter keyword, or contact our support team directly.</p>
          </div>
        )}
      </div>

      {/* Contact CTA */}
      <section className="mt-12 rounded-2xl border-2 border-sarthi-primary/20 bg-gradient-to-br from-sarthi-accent/40 to-white p-6 sm:p-8" data-testid="faq-contact-cta">
        <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-sarthi-text">Still have a question?</h2>
            <p className="mt-1 text-sm font-semibold text-sarthi-muted">
              Reach our support team — we respond to every message within 24 hours (business days).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              data-testid="faq-contact-email"
              href="mailto:contact@sarthiassociates.com"
              className="sarthi-btn-primary inline-flex items-center gap-2 px-5 py-3 text-white"
            >
              <Mail className="h-4 w-4" /> Email support
            </a>
            <a
              data-testid="faq-contact-phone"
              href="tel:+919131847732"
              className="sarthi-btn-outline inline-flex items-center gap-2 px-5 py-3 font-bold"
            >
              <Phone className="h-4 w-4" /> +91 91318 47732
            </a>
            <Link
              data-testid="faq-contact-consultancy"
              to="/consultancy"
              className="sarthi-btn-outline inline-flex items-center gap-2 px-5 py-3 font-bold"
            >
              <MessageSquare className="h-4 w-4" /> Ask consultancy
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
